/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {forwardRef, memo, useCallback} from 'react';
import {useSelector} from 'react-redux';
import {hasAccountIds} from '@ion/core/src/entities/Accounts';
import {translate} from '@ion/core/src/locale/locale';
import {loadLandmarkGeoFences, searchGeoFences} from '@ion/core/src/services/ArcGIS';
import FilterEmptyState from '@ion/dls/src/filters/FilterEmptyState';
import {SideNavGeofenceEventsIcon} from '@ion/dls/src/icons/SideNavGeofenceEventsIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {IconSize} from '@ion/dls/src/types/types';
import changePage, {Route} from '@/routes/RouteDefinitions';
import {setGeofenceFilters} from '@/store/actions/tracking';
import {trackingFiltersSelector} from '@/store/selectors/tracking-selectors';
import {useAncestorIds} from '../useAncestorIds';
import {useGeofenceDescendantIds} from '../useGeofenceDescendantIds';
import TrackingDrawerFilter, {
  TrackingDrawerFilterHandle,
  TrackingDrawerFilterOption
} from './TrackingDrawerFilter';
import {TrackingFilterNavigationRequest, TrackingFilterStateKey} from './types';
import {requestTrackingFilterNavigation} from './utils';

export type TrackingGeofenceFilterHandle = TrackingDrawerFilterHandle;

export interface TrackingGeofenceFilterProps {
  onRequestNavigate?: TrackingFilterNavigationRequest;
  onSelectedCountChange?: (count: number) => void;
  onSelectionChange?: () => void;
}

export const TrackingGeofenceFilter = forwardRef<TrackingGeofenceFilterHandle, TrackingGeofenceFilterProps>(({
  onRequestNavigate,
  onSelectedCountChange,
  onSelectionChange
}, ref) => {
  const ancestorAccountIds  = useAncestorIds();
  const descendantAccountId = useGeofenceDescendantIds();
  const trackingFilters     = useSelector(trackingFiltersSelector);
  const selectedGeofences   = trackingFilters?.geoFences?.selected ?? [];

  const loadOptions = useCallback(async(): Promise<TrackingDrawerFilterOption[]> => {
    // skips the request when there are no account ids to query (e.g. the global group is still loading)...
    const geofences = hasAccountIds(descendantAccountId)
      ? await searchGeoFences({query: loadLandmarkGeoFences(ancestorAccountIds, descendantAccountId)})
      : [];
    return geofences?.map(geofence => ({
      ...geofence,
      label   : geofence?.landmarkName,
      payload : {
        id   : geofence?.OBJECTID,
        name : geofence?.landmarkName
      },
      value : geofence?.OBJECTID
    })) ?? [];
  }, [ancestorAccountIds, descendantAccountId]);

  const toSelectedValue = useCallback(selected => selected?.id ?? selected, []);
  const handleManageGeofences = useCallback(function() {
    requestTrackingFilterNavigation(function() {
      changePage(Route.ADMIN_GEOFENCES);
    }, onRequestNavigate);
  }, [onRequestNavigate]);

  return (
    <TrackingDrawerFilter ref={ref}
      applyFilter={setGeofenceFilters}
      filterStateKey={TrackingFilterStateKey.GeoFences}
      emptyState={(
        <FilterEmptyState title={translate('filters:tracking.geofence.empty.title')}
          description={translate('filters:tracking.geofence.empty.description')}
          actionLabel={translate('filters:tracking.geofence.empty.action')}
          icon={<SideNavGeofenceEventsIcon color={dlsColor('grey1')} size={IconSize.medium} />}
          onAction={handleManageGeofences} />
      )}
      loadOptions={loadOptions}
      selectedItems={selectedGeofences}
      searchPlaceholder={translate('filters:tracking.geofence.searchPlaceholder')}
      toSelectedValue={toSelectedValue}
      onSelectedCountChange={onSelectedCountChange}
      onSelectionChange={onSelectionChange} />
  );
});

export default memo(TrackingGeofenceFilter);
